<script lang="ts">
	import { create, type JsonObject } from "@bufbuild/protobuf";
	import * as EmailValidator from "email-validator";
	import { Helper, Hr, Input, Label, Select, Textarea } from "flowbite-svelte";
	import { ImageSchema, type Image } from "#gen/bundles/v1/common_pb.js";
	import { ContactDataSchema, type ContactData, type ContactDataJson } from "#gen/bundles/v1/contact_pb.js";
	import { states } from "#lib/address.js";
	import Icon from "#lib/components/Icon.svelte";
	import PhoneNumberInput from "#lib/components/PhoneNumberInput.svelte";
	import TogglePane from "#lib/components/TogglePane.svelte";
	import EditImage from "#lib/components/images/EditImage.svelte";
	import { CONTACT_IMAGE_ASPECT_RATIO } from "#lib/components/images/constants.js";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import { validatePhoneNumber } from "#lib/phonenumber.js";
	import { toJson } from "#lib/protobuf-compat.js";
	import { ERR_INVALID_EMAIL, ERR_INVALID_PHONE_NUMBER, ERR_REQUIRED } from "#lib/validation.js";

	interface Props {
		data: ContactData;
		onUpdate?: (data: ContactDataJson) => void;
		onPreview?: (data: ContactDataJson) => void;
	}

	let { data, onUpdate, onPreview }: Props = $props();
	let errors: Record<string, unknown> = $state({});

	function getImage(): Image {
		let image = data.photo;
		if (!image) {
			image = create(ImageSchema, {});
		}
		return image;
	}

	function handlePhotoChange(e: CustomEvent<ImagePickerChangeEvent>) {
		const update = toJson(ContactDataSchema, data) as ContactDataJson;
		const image = getImage();
		image.url = e.detail.value;
		update["photo"] = toJson(ImageSchema, image);
		onUpdate?.(update);
	}

	function handleAddressLinesChange(e: Event, i: number) {
		const target = e.target as HTMLInputElement;
		const update = toJson(ContactDataSchema, data) as ContactDataJson;
		const address = (update["address"] ?? {}) as JsonObject;

		const lines = (address["addressLines"] ?? []) as string[];
		lines.splice(i, 1, target.value);
		address.addressLines = lines;

		update["address"] = address;
		onPreview?.(update);
	}

	function handleAddressChange(e: Event) {
		const target = e.target as HTMLInputElement;
		const update = toJson(ContactDataSchema, data) as ContactDataJson;
		const address = (update["address"] ?? {}) as JsonObject;
		address[target.id] = target.value;
		update["address"] = address;
		onPreview?.(update);
	}

	function handlePhoneNumberInput(id: string, e: CustomEvent<string>) {
		const update = toJson(ContactDataSchema, data) as JsonObject;
		update[id] = e.detail;
		onPreview?.(update);
	}
	function handleInput(e: Event) {
		const target = e.target as HTMLInputElement;
		const update = toJson(ContactDataSchema, data) as JsonObject;
		update[target.id] = target.value;
		onPreview?.(update);
	}

	function validateFullName(): boolean {
		if (!data.fullName) {
			setError("fullName", ERR_REQUIRED);
			return false;
		}
		clearError("fullName");
		onUpdate?.(toJson(ContactDataSchema, data));
		return true;
	}

	function validateWorkNumber(): boolean {
		if (data.workNumber.length > 0 && !validatePhoneNumber(data.workNumber)) {
			setError("workNumber", ERR_INVALID_PHONE_NUMBER);
			return false;
		}
		clearError("workNumber");
		onUpdate?.(toJson(ContactDataSchema, data));
		return true;
	}

	function validateMobileNumber(): boolean {
		if (data.mobileNumber.length > 0 && !validatePhoneNumber(data.mobileNumber)) {
			setError("mobileNumber", ERR_INVALID_PHONE_NUMBER);
			return false;
		}
		clearError("mobileNumber");
		onUpdate?.(toJson(ContactDataSchema, data));
		return true;
	}

	function validateEmail(): boolean {
		if (data.email.length > 0 && !EmailValidator.validate(data.email)) {
			setError("email", ERR_INVALID_EMAIL);
			return false;
		}
		clearError("email");
		onUpdate?.(toJson(ContactDataSchema, data));
		return true;
	}

	function handleChange() {
		onUpdate?.(toJson(ContactDataSchema, data));
	}

	function setError(field: string, error: string) {
		errors[field] = error;
		errors = errors;
	}

	function clearError(field: string) {
		delete errors[field];
		errors = errors;
	}
</script>

<TogglePane enabled={true} label="Your information">
	{#snippet control()}<div></div>{/snippet}
	<div class="flex min-h-0 w-full justify-stretch gap-6">
		<div class="flex h-full w-1/4 flex-none flex-col">
			<Label class="mb-2">Photo</Label>
			<EditImage shape="circle" class="grow rounded-xl border border-gray-300 bg-gray-50 p-8" imgClass="m-auto aspect-square m-auto" dropzoneClass="aspect-square" bind:error={errors["photo"]} value={data.photo?.url} on:change={handlePhotoChange} aspectRatio={CONTACT_IMAGE_ASPECT_RATIO} />
		</div>
		<div class="grid grow grid-cols-2 gap-4">
			<div>
				<Label for="v" class="mb-2">Full name</Label>
				<Input color={errors["fullName"] ? "red" : "base"} name="fullName" id="fullName" value={data.fullName} on:input={handleInput} on:change={validateFullName} />
				{#if errors["fullName"]}
					<Helper class="mt-2" color="red"><span class="font-medium">{errors["fullName"]}</span></Helper>
				{/if}
			</div>
			<div>
				<Label for="title" class="mb-2">Title</Label>
				<Input name="title" id="title" value={data.title} on:input={handleInput} on:change={handleChange} />
			</div>
			<div>
				<Label for="department" class="mb-2">Department</Label>
				<Input name="department" id="department" value={data.department} on:input={handleInput} on:change={handleChange} />
			</div>
			<div>
				<Label for="company" class="mb-2">Company</Label>
				<Input name="company" id="company" value={data.company} on:input={handleInput} on:change={handleChange} />
			</div>
			<div class="col-span-2">
				<Label for="bio" class="mb-2">Bio</Label>
				<Textarea name="bio" id="bio" rows={4} value={data.bio} on:input={handleInput} on:change={handleChange} />
			</div>
		</div>
	</div>
</TogglePane>
<TogglePane enabled={true} label="Contact information">
	{#snippet control()}<div></div>{/snippet}
	<div class="grid grid-cols-3 items-start gap-4">
		<div class="flex h-10 items-center justify-end gap-4">
			<Icon class="h-6 w-6 flex-none" iconSet="heroicons" name="phone" />
			<Label class="grow" for="workNumber">Work number</Label>
		</div>
		<div class="col-span-2">
			<PhoneNumberInput color={errors["workNumber"] ? "red" : "base"} name="workNumber" id="workNumber" value={data.workNumber} on:input={e => handlePhoneNumberInput("workNumber", e)} on:change={validateWorkNumber} />
			{#if errors["workNumber"]}
				<Helper class="mt-2" color="red"><span class="font-medium">{errors["workNumber"]}</span></Helper>
			{/if}
		</div>
		<div class="flex h-10 items-center justify-end gap-4">
			<Icon class="h-6 w-6 flex-none" iconSet="heroicons" name="device-phone-mobile" />
			<Label class="grow" for="mobileNumber">Mobile number</Label>
		</div>
		<div class="col-span-2">
			<PhoneNumberInput color={errors["mobileNumber"] ? "red" : "base"} name="mobileNumber" id="mobileNumber" value={data.mobileNumber} on:input={e => handlePhoneNumberInput("mobileNumber", e)} on:change={validateMobileNumber} />
			{#if errors["mobileNumber"]}
				<Helper class="mt-2" color="red"><span class="font-medium">{errors["mobileNumber"]}</span></Helper>
			{/if}
		</div>
		<div class="flex h-10 items-center justify-end gap-4">
			<Icon class="h-6 w-6 flex-none" iconSet="heroicons" name="envelope" />
			<Label class="grow" for="email">Email</Label>
		</div>
		<div class="col-span-2">
			<Input color={errors["email"] ? "red" : "base"} name="email" id="email" value={data.email} on:input={handleInput} on:change={validateEmail} />
			{#if errors["email"]}
				<Helper class="mt-2" color="red"><span class="font-medium">{errors["email"]}</span></Helper>
			{/if}
		</div>
		<div class="flex h-10 items-center justify-end gap-4">
			<Icon class="h-6 w-6 flex-none" iconSet="heroicons" name="globe-americas" />
			<Label class="grow" for="website">Website</Label>
		</div>
		<Input class="col-span-2" name="website" id="website" value={data.website} on:input={handleInput} on:change={handleChange} />
		<div class="flex h-10 items-center justify-items-end gap-4">
			<Icon class="h-6 w-6 flex-none" iconSet="heroicons" name="map-pin" />
			<Label class="grow" for="address">Address</Label>
		</div>
		<div class="col-span-2 flex flex-col gap-4">
			<div>
				<Label class="mb-2 grow" for="street">Street address</Label>
				<Input type="text" name="street" id="addressLines" value={data.address?.addressLines[0]} on:input={e => handleAddressLinesChange(e, 0)} on:change={handleChange} />
			</div>
			<div>
				<Label class="mb-2 grow" for="street2">Apt, suite, etc</Label>
				<Input type="text" name="street2" id="addressLines" value={data.address?.addressLines[1]} on:input={e => handleAddressLinesChange(e, 1)} on:change={handleChange} />
			</div>
			<div>
				<Label for="locality" class="mb-2">City</Label>
				<Input type="text" name="locality" id="locality" value={data.address?.locality} on:input={handleAddressChange} on:change={handleChange} />
			</div>
			<div class="grid gap-4 md:grid-cols-2">
				<div>
					<Label for="administrativeArea" class="mb-2">State</Label>
					<Select name="administrativeArea" id="administrativeArea" placeholder="" value={data.address?.administrativeArea} on:input={handleAddressChange} on:change={handleChange}>
						<option value="">Choose option ...</option>
						<Hr />
						{#each states as state (state)}
							<option value={state.value} selected={data.address?.administrativeArea === state.value ? true : undefined}>{state.name}</option>
						{/each}
					</Select>
				</div>
				<div>
					<Label for="postalCode" class="mb-2">Zip</Label>
					<Input type="text" name="postalCode" id="postalCode" value={data.address?.postalCode} on:input={handleAddressChange} on:change={handleChange} />
				</div>
			</div>
		</div>
	</div>
</TogglePane>
